import { createSelector } from 'reselect'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import {
  isHotelOrderItem,
  isItemCancelled,
  isLPCHotelOrderItem,
  haveOrdersBeenFetched,
} from 'lib/order/orderUtils'
import {
  evaluateRecentSearchHotelCarousel,
  type RecentSearchHotelPropertyPlace,
  type RecentSearchHotelPurchaseSummary,
  type RecentSearchHotelStay,
} from 'home/recentSearchHotelCarousel/recentSearchHotelCarouselModel'

const EXCLUDED_PURCHASE_STATUSES = new Set([
  'cancelled',
  'refunded',
  'abandoned',
  'payment_failed',
])

function formatStayDate(value: string | { format: (pattern: string) => string } | undefined): string | undefined {
  if (!value) return undefined
  if (typeof value === 'string') return value.slice(0, 10)
  return value.format(ISO_DATE_FORMAT)
}

function stayFromLeHotelItem(item: App.OrderItem): RecentSearchHotelStay | undefined {
  if (isItemCancelled(item) || item.status === 'failed') return undefined
  if (!item.reservation || item.reservation.state === 'cancelled') return undefined
  if (!item.reservation.startDate || !item.reservation.endDate) return undefined

  const destinationPlaceId = item.offer.property?.geoData?.placeId
  const propertyId = destinationPlaceId || !('propertyId' in item.package)
    ? undefined
    : item.package.propertyId

  return {
    checkIn: item.reservation.startDate,
    checkOut: item.reservation.endDate,
    ...(destinationPlaceId ? { destinationPlaceId } : {}),
    ...(propertyId ? { propertyId } : {}),
  }
}

/**
 * Hotel stays that can hide a recent-search carousel.
 * Only an upcoming stay in the searched destination suppresses it.
 * Stays without a canonical place id stay unresolved instead of matching on location text.
 */
export function summariseOrderForRecentSearchHotel(order: App.Order): RecentSearchHotelPurchaseSummary {
  const stays: Array<RecentSearchHotelStay> = []

  for (const item of order.items) {
    if (!isHotelOrderItem(item) && !isLPCHotelOrderItem(item)) continue
    const stay = stayFromLeHotelItem(item)
    if (stay) stays.push(stay)
  }

  for (const item of order.bedbankItems) {
    if (item.status !== 'completed') continue
    const checkIn = formatStayDate(item.checkIn)
    const checkOut = formatStayDate(item.checkOut)
    if (!checkIn || !checkOut) continue
    stays.push({
      checkIn,
      checkOut,
      ...(item.propertyId ? { propertyId: item.propertyId } : {}),
    })
  }

  for (const item of order.accommodationV2Items) {
    if (item.isShadow || item.status === 'cancelled' || item.status === 'failed') continue
    if (!item.checkIn || !item.checkOut) continue
    stays.push({
      checkIn: item.checkIn,
      checkOut: item.checkOut,
      ...(item.propertyId ? { propertyId: item.propertyId } : {}),
    })
  }

  return {
    countsAsPurchase: order.orderSuccess && !EXCLUDED_PURCHASE_STATUSES.has(order.status),
    stays,
  }
}

function propertyPlaceFromRecord(
  record: { fetching?: boolean, error?: unknown, property?: App.Property.Property } | undefined,
): RecentSearchHotelPropertyPlace {
  if (!record || record.fetching) return { status: 'loading' }
  if (record.error || !record.property?.location.placeId) return { status: 'failed' }
  return { status: 'ready', placeId: record.property.location.placeId }
}

const EMPTY_PURCHASES: Array<RecentSearchHotelPurchaseSummary> = []
const EMPTY_PROPERTY_PLACES: Record<string, RecentSearchHotelPropertyPlace> = {}

export const getRecentSearchHotelPurchases = createSelector(
  (state: App.State) => state.orders.orders,
  (orders): Array<RecentSearchHotelPurchaseSummary> => {
    const values = Object.values(orders)
    if (values.length === 0) return EMPTY_PURCHASES
    return values.map(summariseOrderForRecentSearchHotel)
  },
)

export const getRecentSearchHotelPropertyPlaces = createSelector(
  (state: App.State) => state.offer.properties,
  (properties): Record<string, RecentSearchHotelPropertyPlace> => {
    const entries = Object.entries(properties)
    if (entries.length === 0) return EMPTY_PROPERTY_PLACES
    return Object.fromEntries(entries.map(([propertyId, record]) => [
      propertyId,
      propertyPlaceFromRecord(record),
    ]))
  },
)

export const getRecentSearchHotelCarouselDecision = createSelector(
  (state: App.State) => state.auth.account.memberId,
  (state: App.State) => state.search.userRecentSearches,
  (state: App.State) => haveOrdersBeenFetched(state),
  (state: App.State) => getRecentSearchHotelPurchases(state),
  (state: App.State) => state.destination.placeAncestors,
  (state: App.State) => state.destination.fetchingAncestors,
  (state: App.State) => getRecentSearchHotelPropertyPlaces(state),
  (_state: App.State, now: number) => now,
  (_state: App.State, _now: number, today: string) => today,
  (
    memberId,
    searches,
    ordersFetched,
    purchases,
    ancestorPlaceIdsByPlaceId,
    ancestorFetchingByPlaceId,
    propertyPlaces,
    now,
    today,
  ) => evaluateRecentSearchHotelCarousel({
    memberId,
    searches,
    ordersFetched,
    purchases,
    ancestorPlaceIdsByPlaceId,
    ancestorFetchingByPlaceId,
    propertyPlaces,
    now,
    today,
  }),
)
